import React, { useState, useEffect } from "react";
import { Alert, Table, Button, Container, Spinner, Form, Pagination } from "react-bootstrap";
import userService from "../services/user.service";

function formatDeDate(value) {
  if (!value) return "–";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "–";
  return date.toLocaleDateString("de-DE");
}

function formatEuro(value) {
  if (value == null || value === "") return "–";
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "–";
  return amount.toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

const BatchOrderManagerADM = () => {
  const [batchOrders, setBatchOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [apiErrors, setApiErrors] = useState("");
  const [selectedBatchOrders, setSelectedBatchOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("order_id");
  const [sortOrder, setSortOrder] = useState("asc");

  useEffect(() => {
    loadBatchOrders();
  }, [currentPage, searchTerm, sortField, sortOrder]);

  const loadBatchOrders = async () => {
    setApiErrors("");
    setLoading(true);
    try {
      const response = await userService.getBatchOrdersADM({
        page: currentPage,
        search: searchTerm,
        sort: sortField,
        order: sortOrder,
      });
      setBatchOrders(response.data.batchOrders || []);
      setTotalPages(response.data.totalPages || 1);
    } catch (error) {
      setApiErrors("Die Chargenaufträge konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  const toggleBatchOrderSelection = (orderId) => {
    setSelectedBatchOrders((prevSelected) =>
      prevSelected.includes(orderId)
        ? prevSelected.filter((id) => id !== orderId)
        : [...prevSelected, orderId]
    );
  };

  const handleCancelOrders = async () => {
    if (selectedBatchOrders.length === 0) {
      alert("Bitte mindestens einen Chargenauftrag zum Stornieren auswählen.");
      return;
    }

    if (window.confirm("Sollen die ausgewählten Chargenaufträge wirklich storniert werden?")) {
      setLoading(true);
      setApiErrors("");
      try {
        await userService.cancelBatchOrders({ batchOrders: selectedBatchOrders });
        loadBatchOrders();
        setSelectedBatchOrders([]);
      } catch (error) {
        setApiErrors("Die Chargenaufträge konnten nicht storniert werden.");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleSortChange = (field) => {
    const newSortOrder = sortField === field && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(newSortOrder);
  };

  return (
    <Container>
      <h1>Chargenaufträge</h1>
      {apiErrors && <Alert variant="danger">{apiErrors}</Alert>}
      <Form.Group controlId="searchBatchOrders" className="mb-3">
        <Form.Label>Suche in allen Spalten</Form.Label>
        <Form.Control
          type="search"
          placeholder="Suchen…"
          value={searchTerm}
          onChange={handleSearchChange}
          style={{ minHeight: 44 }}
        />
      </Form.Group>
      <Button onClick={handleCancelOrders} disabled={loading} style={{ minHeight: 44 }}>
        Ausgewählte Aufträge stornieren
      </Button>
      {loading && <Spinner animation="border" className="ms-3" />}
      <div className="table-responsive">
        <Table striped bordered hover className="mt-3">
          <thead>
            <tr>
              <th>Auswahl</th>
              <th onClick={() => handleSortChange("order_id")} style={{ cursor: "pointer" }}>Auftrags-ID</th>
              <th onClick={() => handleSortChange("batch_id")} style={{ cursor: "pointer" }}>Chargen-ID</th>
              <th onClick={() => handleSortChange("batch_name")} style={{ cursor: "pointer" }}>Batchname</th>
              <th onClick={() => handleSortChange("order_date")} style={{ cursor: "pointer" }}>Kommissionierungsdatum</th>
              <th onClick={() => handleSortChange("order_quantity")} style={{ cursor: "pointer" }}>Kommissionierte Batchmenge</th>
              <th onClick={() => handleSortChange("buyer_id")} style={{ cursor: "pointer" }}>Käufer-ID</th>
              <th onClick={() => handleSortChange("buyer_name")} style={{ cursor: "pointer" }}>Käufer</th>
              <th onClick={() => handleSortChange("seller_id")} style={{ cursor: "pointer" }}>Verkäufer-ID</th>
              <th onClick={() => handleSortChange("seller_name")} style={{ cursor: "pointer" }}>Verkäufer</th>
              <th onClick={() => handleSortChange("total_price")} style={{ cursor: "pointer" }}>Gesamtpreis netto</th>
              <th onClick={() => handleSortChange("own_provision_net")} style={{ cursor: "pointer" }}>Eigene Provision netto</th>
            </tr>
          </thead>
          <tbody>
            {batchOrders.length > 0 ? (
              batchOrders.map((order) => (
                <tr key={order.order_id}>
                  <td className="text-center align-middle">
                    <input
                      type="checkbox"
                      onChange={() => toggleBatchOrderSelection(order.order_id)}
                      checked={selectedBatchOrders.includes(order.order_id)}
                      aria-label={`Auftrag ${order.order_id} auswählen`}
                      style={{ width: 22, height: 22, margin: 0 }}
                    />
                  </td>
                  <td>{order.order_id}</td>
                  <td>{order.batch_id != null ? order.batch_id : "–"}</td>
                  <td>{order.batch_name || "–"}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{formatDeDate(order.order_date)}</td>
                  <td>{order.order_quantity != null ? order.order_quantity : "–"}</td>
                  <td>{order.buyer_id != null ? order.buyer_id : "–"}</td>
                  <td>{order.buyer_name || "–"}</td>
                  <td>{order.seller_id != null ? order.seller_id : "–"}</td>
                  <td>{order.seller_name || "–"}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{formatEuro(order.total_price)}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{formatEuro(order.own_provision_net)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="12" className="text-center">Keine Chargenaufträge gefunden.</td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      <Pagination>
        {[...Array(totalPages)].map((_, index) => (
          <Pagination.Item
            key={index}
            active={index + 1 === currentPage}
            onClick={() => setCurrentPage(index + 1)}
          >
            {index + 1}
          </Pagination.Item>
        ))}
      </Pagination>
    </Container>
  );
};

export default BatchOrderManagerADM;
